import {
  Box,
  Text,
  Group,
  Avatar,
  ActionIcon,
  Textarea,
  Stack,
  Collapse,
  useMantineTheme,
  Menu,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState, forwardRef } from 'react';
import {
  IconHeart,
  IconHeartFilled,
  IconMessageCircle,
  IconDots,
  IconTrash,
  IconPencil,
  IconFlag,
  IconChevronDown,
  IconChevronUp,
  IconSend,
} from '@tabler/icons-react';
import { Button, Paper } from '@/components/ui';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface Comment {
  id: string;
  author: {
    name: string;
    avatar?: string;
  };
  content: string;
  timestamp: Date | string;
  likes: number;
  liked?: boolean;
  replies?: Comment[];
  isEdited?: boolean;
}

export interface CommentThreadProps {
  comments: Comment[];
  currentUser?: {
    name: string;
    avatar?: string;
  };
  onAddComment?: (content: string, parentId?: string) => void;
  onEditComment?: (id: string, content: string) => void;
  onDeleteComment?: (id: string) => void;
  onLikeComment?: (id: string) => void;
  onReportComment?: (id: string) => void;
  maxDepth?: number;
  showReplies?: boolean;
  placeholder?: string;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
}

const formatRelativeTime = (date: Date | string): string => {
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return then.toLocaleDateString();
};

interface CommentItemProps {
  comment: Comment;
  depth: number;
  maxDepth: number;
  currentUser?: CommentThreadProps['currentUser'];
  onAddComment?: CommentThreadProps['onAddComment'];
  onEditComment?: CommentThreadProps['onEditComment'];
  onDeleteComment?: CommentThreadProps['onDeleteComment'];
  onLikeComment?: CommentThreadProps['onLikeComment'];
  onReportComment?: CommentThreadProps['onReportComment'];
  gradientVariant: 'primary' | 'secondary' | 'tertiary';
}

const CommentItem = ({
  comment,
  depth,
  maxDepth,
  currentUser,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onLikeComment,
  onReportComment,
  gradientVariant,
}: CommentItemProps) => {
  const theme = useMantineTheme();
  const gradient = theme.other?.gradients?.[gradientVariant];
  const [showReplyInput, { toggle: toggleReplyInput }] = useDisclosure(false);
  const [showReplies, { toggle: toggleReplies }] = useDisclosure(true);
  const [replyContent, setReplyContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);

  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const hasReplies = comment.replies && comment.replies.length > 0;
  const canReply = depth < maxDepth;

  const handleReply = () => {
    if (replyContent.trim()) {
      onAddComment?.(replyContent, comment.id);
      setReplyContent('');
      toggleReplyInput();
    }
  };

  const handleEdit = () => {
    if (editContent.trim()) {
      onEditComment?.(comment.id, editContent);
      setIsEditing(false);
    }
  };

  return (
    <Box ml={depth > 0 ? 'md' : 0}>
      <Group align="flex-start" wrap="nowrap">
        <Avatar
          src={comment.author.avatar}
          alt={comment.author.name}
          size="sm"
          radius="xl"
        >
          {comment.author.name.charAt(0).toUpperCase()}
        </Avatar>

        <Box style={{ flex: 1 }}>
          <Paper variant="subtle" p="sm">
            <Group justify="space-between" mb={4}>
              <Group gap="xs">
                <Text size="sm" fw={600} style={accessibilityStyles}>
                  {comment.author.name}
                </Text>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  {formatRelativeTime(comment.timestamp)}
                </Text>
                {comment.isEdited && (
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    (edited)
                  </Text>
                )}
              </Group>

              <Menu shadow="md" width={150}>
                <Menu.Target>
                  <ActionIcon variant="subtle" size="xs">
                    <IconDots size={14} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {currentUser?.name === comment.author.name && onEditComment && (
                    <Menu.Item
                      leftSection={<IconPencil size={14} />}
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Menu.Item>
                  )}
                  {currentUser?.name === comment.author.name && onDeleteComment && (
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      color="red"
                      onClick={() => onDeleteComment(comment.id)}
                    >
                      Delete
                    </Menu.Item>
                  )}
                  {currentUser?.name !== comment.author.name && onReportComment && (
                    <Menu.Item
                      leftSection={<IconFlag size={14} />}
                      onClick={() => onReportComment(comment.id)}
                    >
                      Report
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            </Group>

            {isEditing ? (
              <Stack gap="xs">
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.currentTarget.value)}
                  autosize
                  minRows={2}
                />
                <Group gap="xs">
                  <Button size="xs" onClick={handleEdit}>
                    Save
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => {
                      setIsEditing(false);
                      setEditContent(comment.content);
                    }}
                  >
                    Cancel
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Text size="sm" style={accessibilityStyles}>{comment.content}</Text>
            )}
          </Paper>

          {/* Actions */}
          <Group gap="xs" mt={4} ml={4}>
            <ActionIcon
              variant="subtle"
              size="xs"
              onClick={() => onLikeComment?.(comment.id)}
              c={comment.liked ? 'red' : undefined}
            >
              {comment.liked ? <IconHeartFilled size={14} /> : <IconHeart size={14} />}
            </ActionIcon>
            <Text size="xs" c="dimmed" style={accessibilityStyles}>
              {comment.likes}
            </Text>

            {canReply && currentUser && (
              <ActionIcon variant="subtle" size="xs" onClick={toggleReplyInput}>
                <IconMessageCircle size={14} />
              </ActionIcon>
            )}

            {hasReplies && (
              <ActionIcon variant="subtle" size="xs" onClick={toggleReplies}>
                {showReplies ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
              </ActionIcon>
            )}

            {hasReplies && (
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                {comment.replies!.length} {comment.replies!.length === 1 ? 'reply' : 'replies'}
              </Text>
            )}
          </Group>

          {/* Reply input */}
          <Collapse in={showReplyInput}>
            <Group gap="xs" mt="xs" align="flex-start">
              <Avatar src={currentUser?.avatar} size="xs" radius="xl">
                {currentUser?.name?.charAt(0).toUpperCase()}
              </Avatar>
              <Textarea
                placeholder="Write a reply..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.currentTarget.value)}
                autosize
                minRows={1}
                style={{ flex: 1 }}
              />
              <ActionIcon
                variant="filled"
                color={gradient?.from}
                onClick={handleReply}
                disabled={!replyContent.trim()}
              >
                <IconSend size={14} />
              </ActionIcon>
            </Group>
          </Collapse>

          {/* Nested replies */}
          <Collapse in={showReplies}>
            {hasReplies && (
              <Stack gap="sm" mt="sm">
                {comment.replies!.map((reply) => (
                  <CommentItem
                    key={reply.id}
                    comment={reply}
                    depth={depth + 1}
                    maxDepth={maxDepth}
                    currentUser={currentUser}
                    onAddComment={onAddComment}
                    onEditComment={onEditComment}
                    onDeleteComment={onDeleteComment}
                    onLikeComment={onLikeComment}
                    onReportComment={onReportComment}
                    gradientVariant={gradientVariant}
                  />
                ))}
              </Stack>
            )}
          </Collapse>
        </Box>
      </Group>
    </Box>
  );
};

const CommentThread = forwardRef<HTMLDivElement, CommentThreadProps>(
  (
    {
      comments,
      currentUser,
      onAddComment,
      onEditComment,
      onDeleteComment,
      onLikeComment,
      onReportComment,
      maxDepth = 3,
      showReplies = true,
      placeholder = 'Write a comment...',
      gradientVariant = 'primary',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const [newComment, setNewComment] = useState('');

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const handleAddComment = () => {
      if (newComment.trim()) {
        onAddComment?.(newComment);
        setNewComment('');
      }
    };

    return (
      <Box ref={ref}>
        {/* New comment input */}
        {currentUser && (
          <Group gap="sm" mb="lg" align="flex-start">
            <Avatar src={currentUser.avatar} size="sm" radius="xl">
              {currentUser.name.charAt(0).toUpperCase()}
            </Avatar>
            <Textarea
              placeholder={placeholder}
              value={newComment}
              onChange={(e) => setNewComment(e.currentTarget.value)}
              autosize
              minRows={2}
              style={{ flex: 1 }}
            />
            <Button
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              gradientVariant={gradientVariant}
            >
              Post
            </Button>
          </Group>
        )}

        {/* Comments list */}
        <Stack gap="md">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              depth={0}
              maxDepth={maxDepth}
              currentUser={currentUser}
              onAddComment={onAddComment}
              onEditComment={onEditComment}
              onDeleteComment={onDeleteComment}
              onLikeComment={onLikeComment}
              onReportComment={onReportComment}
              gradientVariant={gradientVariant}
            />
          ))}
        </Stack>

        {comments.length === 0 && (
          <Text ta="center" c="dimmed" py="xl" style={accessibilityStyles}>
            No comments yet. Be the first to comment!
          </Text>
        )}
      </Box>
    );
  }
);

CommentThread.displayName = 'CommentThread';

export default CommentThread;
